export type ReportId = "pl" | "bs" | "cf" | "tb" | "gl" | "ar" | "ap" | "vat" | "tds";

export const REPORT_GROUPS = ["Statements", "Receivables and payables", "Tax"] as const;

export interface ReportEntry {
  id: ReportId;
  label: string;
  icon: string;
  to: string;
  description: string;
  group: (typeof REPORT_GROUPS)[number];
}

/** The reports that exist, as the library and the rail list them. */
export const REPORTS: ReportEntry[] = [
  {
    id: "pl",
    label: "Profit and loss",
    icon: "TrendingUp",
    to: "/reports/profit-and-loss",
    description: "Income and expenses for the fiscal year, against the year before.",
    group: "Statements",
  },
  {
    id: "bs",
    label: "Balance sheet",
    icon: "Scale",
    to: "/reports/balance-sheet",
    description: "Assets, liabilities and equity at a point in time.",
    group: "Statements",
  },
  {
    id: "cf",
    label: "Cash flow",
    icon: "Waves",
    to: "/reports/cash-flow",
    description: "Movement in cash across operating, investing and financing activities.",
    group: "Statements",
  },
  {
    id: "tb",
    label: "Trial balance",
    icon: "ListTree",
    to: "/reports/trial-balance",
    description: "Every account with movement, with debit and credit totals proved.",
    group: "Statements",
  },
  {
    id: "gl",
    label: "General ledger",
    icon: "BookOpen",
    to: "/reports/general-ledger",
    description: "Transaction-level detail for one account with a running balance.",
    group: "Statements",
  },
  {
    id: "ar",
    label: "Receivables ageing",
    icon: "ArrowDownLeft",
    to: "/reports/receivables-ageing",
    description: "What customers owe, by how long it has been owed.",
    group: "Receivables and payables",
  },
  {
    id: "ap",
    label: "Payables ageing",
    icon: "ArrowUpRight",
    to: "/reports/payables-ageing",
    description: "What is owed to suppliers, by how long it has been owed.",
    group: "Receivables and payables",
  },
  {
    id: "vat",
    label: "VAT return",
    icon: "Percent",
    to: "/reports/vat-return",
    description: "Output and recoverable input VAT for a month, with the amount payable.",
    group: "Tax",
  },
  {
    id: "tds",
    label: "TDS withheld",
    icon: "Banknote",
    to: "/reports/tds-withheld",
    description: "Tax withheld on supplier payments, and withheld from you by customers.",
    group: "Tax",
  },
];

const STARRED_KEY = "rigo.accounting.reports.starred";

/** A viewer's own shortcuts: kept in this browser, and the page works without them. */
export function readStarred(): ReportId[] {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(STARRED_KEY) ?? "[]");
    return Array.isArray(stored)
      ? (stored.filter((id) => REPORTS.some((r) => r.id === id)) as ReportId[])
      : [];
  } catch {
    return [];
  }
}

export function writeStarred(ids: ReportId[]): void {
  try {
    localStorage.setItem(STARRED_KEY, JSON.stringify(ids));
  } catch {
    // Private windows may refuse. A star that does not stick costs nothing else.
  }
}
